const resolvePath = (object, path, defaultValue) => path
    .split('.')
    .reduce((o, p) => o ? o[p] : defaultValue, object)
export function lollipop(protein_seq, pfams, options, summarized_aa) {
    let chart = {};
    const container_id = options?.container_id || "lollipop-plot";

    const getContainerWidth = () => document.getElementById(container_id)?.clientWidth || 600;

    chart.options = {
        container_id,
        margin: { top: 20, right: 20, bottom: 40, left: 50 },
        svg_width: getContainerWidth(),
        svg_height: 360,
        seqplot_height: 30,
        domain_height: 16,
        seq_height: 6,
        xCat: "position",
        yCat: "cohort_num",
        domain_colors: d3.scaleOrdinal(d3.schemeCategory10)
    };

    for (let opt in options) {
        chart.options[opt] = options[opt];
    }

    let chart_width = chart.options.svg_width - chart.options.margin.left - chart.options.margin.right,
        chart_height = chart.options.svg_height - chart.options.margin.top - chart.options.margin.bottom,
        yplot_height = chart_height - chart.options.seqplot_height;

    const xMax = protein_seq.length,
        xMin = 1;
    let seqarray = protein_seq.split("");

    let xScale = d3.scaleLinear()
        .range([0, chart_width])
        .domain([xMin, xMax])
        .nice();

    let xAxis = d3.axisBottom(xScale);
    let yScale, yAxis, gX, gY, ptmLines, ptmSites, seqline, domains, domainannotation, letters;

    const svg = d3.select("#" + chart.options.container_id)
        .append("svg")
        .attr("width", chart.options.svg_width)
        .attr("height", chart.options.svg_height)
        .style("background-color", "white")
        .append("g")
        .attr("transform", `translate(${chart.options.margin.left},${chart.options.margin.top})`);

    svg.append("defs")
        .append("clipPath")
        .attr("id", "clip")
        .append("rect")
        .attr("width", chart_width)
        .attr("height", chart_height + chart.options.margin.top)
        .attr("x", 0)
        .attr("y", -chart.options.margin.top);

    gX = svg.append("g")
        .classed("xAxis", true)
        .attr("transform", `translate(0,${chart_height})`)
        .call(xAxis);

    svg.append("text")
        .attr("class", "x label")
        .attr("text-anchor", "middle")
        .attr("x", chart_width / 2)
        .attr("y", chart_height + 30)
        .text("Residue number")
        .style("font-size", "13px")
        .style("font-family", "Arial");

    svg.append("text")
        .attr("class", "y label")
        .attr("text-anchor", "middle")
        .attr("y", -chart.options.margin.left)
        .attr("x", -yplot_height / 2)
        .attr("dy", "0.8em")
        .attr("transform", "rotate(-90)")
        .text("Number")
        .style("font-size", "13px")
        .style("font-family", "Arial");

    let mask = svg.append("g").attr("clip-path", "url(#clip)");

    mask.append("rect")
        .attr("width", chart_width)
        .attr("height", chart_height)
        .style("fill", "none")
        .style("pointer-events", "all")
        .call(d3.zoom()
            .scaleExtent([0.8, 4 / 120 * protein_seq.length])
            .translateExtent([[0, 0], [chart_width, chart_height]])
            .on("zoom", zoomed));

    const canvas = mask.append("g");
    const gLines = canvas.append("g").classed("g-ptm-line", true);
    const gSites = canvas.append("g").classed("g-ptm-circle", true);

    chart.draw_ptm = function () {
        const yMax = Math.max(...summarized_aa.map(d => d.total_count));
        yScale = d3.scaleLinear().range([yplot_height, 0]).domain([0, yMax]).nice();
        yAxis = d3.axisLeft(yScale).tickFormat(d => d < 0 || !Number.isInteger(d) ? '' : d);

        if (!gY) {
            gY = svg.append("g").classed("y-axis", true).call(yAxis);
        } else {
            gY.call(yAxis);
        }

        gLines.html(null);
        ptmLines = gLines.selectAll("line")
            .data(summarized_aa)
            .enter().append("line")
            .classed("ptm-line", true)
            .attr("stroke", "#666")
            .attr("stroke-width", 1)
            .attr("x1", d => xScale(d.AA_Position))
            .attr("x2", d => xScale(d.AA_Position))
            .attr("y1", yplot_height + chart.options.domain_height / 2 - chart.options.seq_height / 2)
            .attr("y2", d => yScale(d.total_count));

        const tooltip = d3.select("#lp-tooltip");
        const evidenceLabelMap = {
            "text-mining": "Text mining",
            "figure-mining": "Figure mining",
            "experiment": "PTMAtlas datasets",
            "cohort": "CPTAC datasets",
        };


        function showTooltip(d) {
            let html = '<table border="1"><tr><th>PTM site</th><th>Type</th><th>Evidence Class</th></tr>';
            for (const [type, values] of Object.entries(d.Mutation_Type)) {
                const content = Object.entries(values)
                    .map(([cls, cnt]) => {
                        const label = evidenceLabelMap[cls] || cls;
                        return `${label}: ${cnt}`;
                    })
                    .join("<br>");

                // const content = Object.entries(values).map(([cls, cnt]) => `${cls}:${cnt}`).join(";<br>");
                html += `<tr><td>${d.Protein_Change}</td><td>${type}</td><td>${content}</td></tr>`;
            }
            html += "</table>";
            tooltip.html(html).style("display", "block");
            const width = tooltip.node().offsetWidth;
            tooltip.style("left", `${d3.event.x - width - 10}px`)
                .style("top", `${d3.event.y - 30}px`);
        }

        function hideTooltip() {
            tooltip.style("display", "none");
        }

        gSites.html(null);
        ptmSites = gSites.selectAll("circle")
            .data(summarized_aa)
            .enter().append("circle")
            .classed("ptm-circle", true)
            .attr("r", 4)
            .attr("cx", d => xScale(d.AA_Position))
            .attr("cy", d => yScale(d.total_count))
            .style("fill", "#000")
            .style("stroke", "#666")
            .style("stroke-width", 1)
            .on("mouseover", function (d) { d3.select(this).attr("r", 6); showTooltip(d); })
            .on("mouseout", function (d) { d3.select(this).attr("r", 4); hideTooltip(d); });
    };

    chart.draw_ptm();

    seqline = canvas.append("g").append("rect")
        .attr("x", xScale(1))
        .attr("y", yplot_height + chart.options.domain_height / 2 - chart.options.seq_height / 2)
        .attr("width", xScale(protein_seq.length) - xScale(1))
        .attr("height", chart.options.seq_height)
        .style("fill", "white")
        .style("stroke", "#666");

    domains = canvas.append("g")
        .selectAll("rect")
        .data(pfams)
        .enter().append("rect")
        .attr("x", d => xScale(d.start))
        .attr("y", yplot_height)
        .attr("width", d => xScale(d.end) - xScale(d.start))
        .attr("height", chart.options.domain_height)
        .style("fill", d => chart.options.domain_colors(d.id))
        .style("stroke", "#666");

    domainannotation = canvas.append("g")
        .selectAll("text")
        .data(pfams)
        .enter().append("a")
        .attr("target", "_blank")
        .attr("xlink:href", d => `http://pfam.xfam.org/family/${d.id}`)
        .append("text")
        .attr("y", yplot_height + 10)
        .attr("x", d => (xScale(d.end) + xScale(d.start)) / 2)
        .attr("text-anchor", "middle")
        .style("font-size", "12px")
        .style("cursor", "pointer")
        .text(d => d.description?.substring(0, Math.floor(xScale(d.end) - xScale(d.start)) / 9) || d.id);

    letters = canvas.append("g")
        .selectAll("text")
        .data(seqarray)
        .enter().append("text")
        .attr("y", chart_height - 2)
        .attr("x", (d, i) => xScale(i + 1))
        .attr("text-anchor", "middle")
        .style("font-size", "13px")
        .attr("visibility", "hidden")
        .text(d => d);

    function zoomed() {
        let new_xScale = d3.event.transform.rescaleX(xScale);
        const range = new_xScale.domain();
        new_xScale.domain([Math.max(range[0], xMin), Math.min(range[1], xMax)]);
        gX.call(xAxis.scale(new_xScale));
        ptmSites.attr("cx", d => new_xScale(d.AA_Position));
        ptmLines.attr("x1", d => new_xScale(d.AA_Position)).attr("x2", d => new_xScale(d.AA_Position));
        seqline.attr("x", new_xScale(1)).attr("width", new_xScale(protein_seq.length) - new_xScale(1));
        domains.attr("x", d => new_xScale(d.start)).attr("width", d => new_xScale(d.end) - new_xScale(d.start));
        domainannotation.attr("x", d => (new_xScale(d.end) + new_xScale(d.start)) / 2);
        letters.attr("x", (d, i) => new_xScale(i + 1))
            .attr("visibility", new_xScale.domain()[1] - new_xScale.domain()[0] >= 60 ? "hidden" : "visible");
    }

    chart.resize = function () {
        chart.options.svg_width = getContainerWidth();
        chart_width = chart.options.svg_width - chart.options.margin.left - chart.options.margin.right;
        xScale.range([0, chart_width]);
        xAxis = d3.axisBottom(xScale);
        gX.call(xAxis);
        d3.select("#" + container_id + " svg").attr("width", chart.options.svg_width);
        ptmSites.attr("cx", d => xScale(d.AA_Position));
        ptmLines.attr("x1", d => xScale(d.AA_Position)).attr("x2", d => xScale(d.AA_Position));
        seqline.attr("x", xScale(1)).attr("width", xScale(protein_seq.length) - xScale(1));
        domains.attr("x", d => xScale(d.start)).attr("width", d => xScale(d.end) - xScale(d.start));
        domainannotation.attr("x", d => (xScale(d.end) + xScale(d.start)) / 2);
        letters.attr("x", (d, i) => xScale(i + 1));
    };

    window.addEventListener("resize", chart.resize);

    return chart;
}


